import type { Appointment, PractitionerRole, Schedule } from "fhir/r5";

/** Mesma URL usada em ManagePractitioners e useFhirData para extensão de associação por CNPJ. */
export const PRACTITIONER_ROLE_ASSOCIATION_URL =
  "https://fluxmed.cloud/StructureDefinition/PractitionerRoleAssociation";

export function onlyDigitsAssociation(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

/**
 * Lê o CNPJ (somente dígitos) da extensão PractitionerRoleAssociation no recurso FHIR.
 * Retorna null se não houver vínculo de associação.
 */
export function getAssociationCnpjDigitsFromRoleResource(
  resource?: PractitionerRole | null,
): string | null {
  const ext = resource?.extension?.find(
    (e) => e?.url === PRACTITIONER_ROLE_ASSOCIATION_URL,
  ) as { extension?: Array<{ url?: string; valueString?: string }> } | undefined;
  if (!ext || !Array.isArray(ext.extension)) return null;

  const cnpjDigits = onlyDigitsAssociation(
    ext.extension?.find((x) => x?.url === "cnpj")?.valueString,
  );
  if (!cnpjDigits) return null;
  return cnpjDigits;
}

/**
 * Conjunto de referências `Practitioner/...` cujo PractitionerRole está vinculado ao mesmo CNPJ
 * da associação (extensão). Usado para filtrar agendas no cliente quando a API não filtra por extensão.
 *
 * Limitação: o bundle FHIR ainda pode trazer mais dados na rede; o filtro evita exibir/operar fora do escopo.
 */
export function practitionerRefsSharingAssociationCnpj(
  practitionerRoleEntries: Array<{ resource?: PractitionerRole }> | undefined,
  cnpjDigits: string | undefined,
): Set<string> {
  const digits = onlyDigitsAssociation(cnpjDigits || "");
  const refs = new Set<string>();
  if (!digits || !practitionerRoleEntries?.length) return refs;

  for (const entry of practitionerRoleEntries) {
    const assoc = getAssociationCnpjDigitsFromRoleResource(entry?.resource);
    if (assoc !== digits) continue;
    const raw = entry?.resource?.practitioner?.reference?.trim();
    if (!raw) continue;
    const normalized = raw.startsWith("Practitioner/")
      ? raw
      : `Practitioner/${raw}`;
    refs.add(normalized);
  }
  return refs;
}

/** Indica se o Schedule tem ao menos um ator Practitioner permitido. */
export function scheduleTouchesAllowedPractitioners(
  schedule: Schedule | undefined,
  allowedPractitionerRefs: Set<string>,
): boolean {
  if (!schedule?.actor?.length || allowedPractitionerRefs.size === 0)
    return false;
  return schedule.actor.some((actor) => {
    const ref = actor?.reference;
    if (!ref?.includes("Practitioner/")) return false;
    return allowedPractitionerRefs.has(ref);
  });
}

/** Filtra entries de bundle mantendo apenas Appointments cujo profissional (participant) está no conjunto. */
export function filterAppointmentBundleByPractitionerRefs(
  bundle: { entry?: Array<{ resource?: unknown }> } | undefined,
  allowedPractitionerRefs: Set<string>,
): { entry?: Array<{ resource?: unknown }> } | undefined {
  if (!bundle?.entry?.length || allowedPractitionerRefs.size === 0)
    return bundle;

  const filtered = bundle.entry.filter((entry) => {
    const res = entry.resource as Appointment | undefined;
    if (res?.resourceType !== "Appointment") return true;
    const participants = res.participant || [];
    return participants.some((p) => {
      const ref = p.actor?.reference;
      if (!ref?.includes("Practitioner/")) return false;
      return allowedPractitionerRefs.has(ref);
    });
  });

  return { ...bundle, entry: filtered };
}
